import { useMemo } from 'react';
import {
  calculatePathProgress,
  getDisplayedCourseInfo,
  getCompletedCourses,
  isCourseCompleted,
  getCourseProgress,
  getCompletedContentForCourse
} from '../utils/progressCalculations';
import { isPathCompleted } from '../utils/contentNavigation';

/**
 * Custom hook for managing student progress state and calculations
 * @param {Object} userProgress - User's progress data from Redux
 * @param {Array} pathCourses - Array of courses in the learning path
 * @param {Object} userCurrentCourse - Currently active course
 * @param {Object} userCurrentContent - Currently active content
 * @returns {Object} Progress state and helper functions
 */
export const useStudentProgress = (userProgress, pathCourses, userCurrentCourse, userCurrentContent) => {
  // Calculate total steps in the learning path
  const totalSteps = useMemo(() => pathCourses?.length || 0, [pathCourses]);

  // Calculate main progress metrics
  const progressMetrics = useMemo(() => {
    if (!pathCourses || !pathCourses.length) {
      return { pathProgress: 0, currentStep: 1, courseProgress: 0 };
    }
    return calculatePathProgress(pathCourses, userProgress, userCurrentCourse);
  }, [pathCourses, userProgress, userCurrentCourse]);

  const { pathProgress, currentStep, courseProgress } = progressMetrics;

  // Calculate displayed course information
  const displayedCourseInfo = useMemo(() => {
    return getDisplayedCourseInfo(userCurrentCourse, pathCourses, userProgress, courseProgress, currentStep);
  }, [userCurrentCourse, pathCourses, userProgress, courseProgress, currentStep]);

  const { displayedCourse, displayedCourseName } = displayedCourseInfo;

  // Helper functions with memoized dependencies
  const progressHelpers = useMemo(() => ({
    getCompletedCoursesLocal: () => getCompletedCourses(pathCourses, userProgress),
    isCourseCompletedLocal: (courseId) => isCourseCompleted(courseId, pathCourses, userProgress),
    getCourseProgressLocal: (courseId) => getCourseProgress(courseId, pathCourses, userProgress),
    getCompletedContentForCourseLocal: (courseId) => getCompletedContentForCourse(courseId, pathCourses, userProgress),
    isPathCompletedLocal: () => {
      if (!pathCourses || !userProgress) return false;
      return isPathCompleted(pathCourses, userProgress, () => getCompletedCourses(pathCourses, userProgress));
    }
  }), [pathCourses, userProgress]);

  // Debug course completion status
  const debugCourseStatus = useMemo(() => {
    if (!pathCourses || pathCourses.length === 0) return [];

    return pathCourses.map(course => {
      const isCompleted = progressHelpers.isCourseCompletedLocal(course._id);
      const isCurrent = userCurrentCourse?._id === course._id;
      const completedLessonIds = userProgress?.completedLessons?.map(cl => {
        try {
          if (typeof cl.lesson === 'object' && cl.lesson && cl.lesson._id) {
            return cl.lesson._id; // Old structure
          }
          return cl.lesson; // New structure (just the lesson ID)
        } catch (error) {
          return null;
        }
      }).filter(id => id !== null) || [];

      return {
        courseId: course._id,
        title: course.title,
        isCompleted,
        isCurrent,
        completedLessons: completedLessonIds.length
      };
    });
  }, [pathCourses, userCurrentCourse, userProgress, progressHelpers]);

  return {
    // Progress metrics
    pathProgress,
    currentStep,
    courseProgress,
    totalSteps,

    // Displayed course info
    displayedCourse,
    displayedCourseName,

    // Helper functions
    ...progressHelpers,

    // Debug info
    debugCourseStatus,

    // Raw data
    userProgress,
    pathCourses,
    userCurrentCourse,
    userCurrentContent
  };
};